/**
 * @flow
 */

import uuid from 'uuid';

type Request = {
  request: true;
  type: string;
  payload: Object;
  id: string;
};

type Response = {
  response: true;
  id: string;
  error: ?string;
  data: ?any;
};

// custom 'MessageEvent' type for Messenger
declare class MessengerEvent extends Event {
  data: Request | Response;
  origin: string;
  lastEventId: string;
  source: WindowProxy;
}

type RequestHandler = {
  type: string;
  handler: (evt: MessengerEvent) => void;
};

export default class Messenger {
  _handlers: Array<RequestHandler>;
  _win: any;

  constructor(win: any) {
    this._win = win;
    this._handlers = [];
  }

  on(type: string, callback: Function) {
    const handledTypes = this._handlers.map(
      (h: RequestHandler): string => h.type
    );

    // one handler per type
    if (handledTypes.includes(type)) {
      return;
    }

    const requestHandler = {
      type,
      handler: (event: MessengerEvent): void => {
        // only interested in requests
        if (event.data.response) {
          return;
        }

        const req: Request = event.data;

        if (req.type === type) {
          const data = callback(req.payload) || null;
          const response: Response = {
            response: true,
            id: req.id,
            data,
            error: null
          };

          // no reply on 'DESTROY:POPUP' actions
          if (event.source) {
            event.source.postMessage(response, '*');
          }
        }
      }
    };

    this._win.addEventListener('message', requestHandler.handler);
    this._handlers.push(requestHandler);
  }

  destroy() {
    this._handlers.forEach((handler: RequestHandler): void => {
      this._win.removeEventListener('message', handler.handler);
    });
  }

  /**
   * @param {any} destination - target window (parent, iframe.contentWindow)
   * @param {string} type - action type
   * @param {string} payload - action payload
   *
   * @return {Promise} - return a promise that resolves the server's response
   */
  send(destination: any, type: string, payload: Object): Promise<any> {
    const id: string = uuid();
    const request: Request = {
      request: true,
      type,
      payload,
      id
    };

    destination.postMessage(request, '*');

    // waiting for response
    const getResponse = new Promise((resolve, reject) => {
      const handler = (event: MessengerEvent): void => {
        // only interested in responses
        if (event.data.request) {
          return;
        }

        const res: Response = event.data;

        if (res.id != id) {
          return;
        }

        if (res.error) {
          reject(res.error);
        }

        else {
          resolve(res.data);
          this._win.removeEventListener('message', handler);
        }
      };

      this._win.addEventListener('message', handler);
    });

    const timedOut = new Promise((resolve, reject) => {
      setTimeout(() => {
        reject('Request timed out');
      }, 2000);
    });

    // TODO catch remove listener as well
    // TODO better way to remove the listener
    return Promise.race([getResponse, timedOut]);
  }
}



// WEBPACK FOOTER //
// ./src/lib/messenger.js